import { UserSessionQuery } from './__generated__/types';
import { Authenticated, AuthenticationError, AuthenticationState } from './AuthContext';
import { GraphqlClientResponse } from './graphql';

export type TrackError = (
  label: string,
  error?: Error,
  data?: {
    [k: string]: unknown;
  },
) => void;

function convertHumanToUser(
  human: NonNullable<NonNullable<UserSessionQuery['web']['userSession']>['human']>,
  trackError: TrackError | undefined,
): Authenticated | AuthenticationError {
  // TODO: Remove this check (see feature request here: https://github.com/opendoor-labs/code/pull/35285)
  if (!human.email) {
    const message = 'invalid data returned: missing Human.email';
    trackError && trackError(message);
    return {
      state: 'error',
      user: undefined,
      message,
    };
  }

  const fullName = human.full_name || human.first_name || 'Customer';
  const firstName = human.first_name || human.full_name || 'Customer';

  const hasOpendoorEmail = human.email.endsWith('@opendoor.com');
  const hasOpendoorInternalAccess = human.opendoor_internal_access || false;
  const impersonated = human.impersonated || false;

  return {
    state: 'authenticated',
    user: {
      humanToken: human.token,
      customerUuid: human.customer_uuid || null,
      email: human.email,
      fullName,
      firstName,
      hasOpendoorEmail,
      hasOpendoorInternalAccess,
      primaryPhone: human.primary_phone || null,
      impersonated,
    },
  };
}

export const parseAuthentication = (
  queryResult: {
    data: GraphqlClientResponse<UserSessionQuery>['data'];
    error?: GraphqlClientResponse<UserSessionQuery>['error'];
    loading: boolean;
  },
  trackError: TrackError | undefined,
): AuthenticationState => {
  if (queryResult.loading) {
    return {
      state: 'authenticating',
      user: undefined,
    };
  }
  if (queryResult.error) {
    trackError && trackError('UserSession error', queryResult.error);
    return {
      state: 'error',
      user: undefined,
      message: queryResult.error.message,
    };
  }
  const userSession = queryResult.data?.web.userSession;
  if (!userSession) {
    // If UserSession is missing, the server returned invalid data.
    const message = 'invalid data returned: missing UserSession';
    trackError && trackError(message);
    return {
      state: 'error',
      user: undefined,
      message,
    };
  }

  if (!userSession.human) {
    return {
      state: 'not_authenticated',
      user: null,
    };
  }

  return convertHumanToUser(userSession.human, trackError);
};
